import { addEventParam, INinjaEvent, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import { IAdaptiveActionCard } from '@ninjacart/nc-nk-core';
import {
  handleMarketingEvents,
  KissanEventType,
  updateFollowStatusApi,
  useGlobalReportContext,
  useHomeContext,
  useProfileStore,
} from '@ninjacart/nc-nk-store';
import { NoSearchResult, Paragraph, Separator, Spinner, View, YStack } from '@ninjacart/nc-nk-ui';
import { UniversalList } from '@ninjacart/nc-nk-web-deps';
// import type { ViewToken } from '@shopify/flash-list';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
// import { ScrollHandlerProcessed } from 'react-native-reanimated';
import { HomeHeader } from './header/homeHeader';
import { ListItemRenderer } from './listItemRenderer';

// const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);

interface FeedListProps {
  data: IAdaptiveActionCard[];
  refreshing: boolean;
  onRefresh: () => void;
  onEndReached: () => void;
  onEndReachedSpinner: boolean;
  onCommentBottomSheetOpen: (item: IAdaptiveActionCard, index: number) => void;
  scrollHandler?: any;
  headerHeight: number;
  setHomeFeedsCard: (feeds: IAdaptiveActionCard[]) => void;
  setExtraData: (value: boolean) => void;
  extraData?: boolean;
  locationPermissionAsk?: () => void;
  isWeatherWidgetAvailable?: boolean;
  flashListRef;
  cropPreference?: any;
  animalPreference?: any[];
  globalSearchEnabled?: boolean;
}

export const FeedList = ({
  data,
  refreshing,
  onRefresh,
  onEndReached,
  onEndReachedSpinner,
  onCommentBottomSheetOpen,
  // scrollHandler,
  headerHeight,
  setHomeFeedsCard,
  setExtraData,
  extraData,
  locationPermissionAsk,
  isWeatherWidgetAvailable,
  flashListRef,
  cropPreference,
  animalPreference,
  globalSearchEnabled,
}: FeedListProps) => {
  // console.log('SSR: Trying to feed list!!!');
  const scrollToIndexRef = useRef(-1);
  const hasImpressionShowRef = useRef<string | number | null>(null);
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const [needRefresh, setNeedRefresh] = useState<boolean>(false);
  const userProfile = useProfileStore((state) => state.userProfile);
  const { t } = useTranslation();
  const scrollOffsetRef = useRef(-1);
  const paginationCount = useRef({
    stream: Platform.OS === 'web' ? 3 : 10,
    shortVideo: Platform.OS === 'web' ? 5 : 10,
  });

  const homeContextFields = useHomeContext([
    'videoFeedIndex',
    // 'isViewableConfigEnabled',
    // 'isMomentumScrollEnded',
    'imagePriorityIndex',
    'scrollToIndex',
    'disableScroll',
  ]);
  // const isMomentumScrollEnded = homeContextFields.isMomentumScrollEnded.get as boolean;
  // const isViewableConfigEnabled = homeContextFields.isViewableConfigEnabled.get as boolean;
  const setVideoFeedIndex = homeContextFields.videoFeedIndex.set;
  const scrollToIndex = homeContextFields.videoFeedIndex.get as number;
  const disableScrollSet = homeContextFields.disableScroll.set;
  const disableScroll = homeContextFields.disableScroll.get;
  // console.log('🚀 ~ disableScroll:', disableScroll);
  // const isViewableCallbackEnabled = useRef(false);
  // const previousScrollY = useSharedValue(0);
  // const headerOffset = useSharedValue(0);

  const reportfields = useGlobalReportContext(['reportIndex', 'openReportSheet']);
  const setCurrentIndex = reportfields['reportIndex'].set;

  useEffect(() => {
    if (cropPreference?.length > 0 || animalPreference?.length! > 0) {
      setExtraData(!extraData);
      return;
    }
  }, [cropPreference, animalPreference]);

  const onReportBottomSheetOpen = (item, index) => {
    setCurrentIndex(index);
    reportfields['openReportSheet'].set(true);
    const itemReportEvent: INinjaEvent = {
      name: KissanEventType.ITEM_REPORTED,
      screenDetails: { screen: 'feed page' },
      eventParams: [
        addEventParam('itemIndex', index),
        addEventParam('recommendationId', item?.recommendationId),
      ],
    };
    enqueue(itemReportEvent);
  };

  // Viewability config for detecting visible items
  const viewabilityConfig = useRef({
    waitForInteraction: true,
    itemVisiblePercentThreshold: 80,
    minimumViewTime: 1000,
  }).current;

  const getMinMaxIndexes = (viewableItems: any[], itemType?: string) => {
    return viewableItems.reduce(
      (acc, item) => {
        if (item.index !== null) {
          if (item.index < acc.activeVideoIndex && item?.item?.type === 'STREAM') {
            acc.activeVideoIndex = item.index;
          }

          if (item.index < acc.min) {
            acc.min = item.index;
          }

          if (item.index > acc.max) {
            acc.max = item.index;
          }

          acc.type = item?.item?.type;
        }

        return acc;
      },
      {
        min: Number.MAX_SAFE_INTEGER,
        max: -1,
        type: itemType,
        activeVideoIndex: Number.MAX_SAFE_INTEGER,
      }
    );
  };

  const handleVideoScrollEvent = (activeIndex, itemValue) => {
    const additionalDetails = [
      addEventParam('scrolledIndex', activeIndex),
      addEventParam('recommendationId', itemValue?.actionData?.content?.recommendationId),
    ];

    const videoScrollEvent: INinjaEvent = {
      name: KissanEventType.REELS_SCROLL_VERTICAL_PAGE_END,
      screenDetails: { screen: 'feed page' },
      eventParams: additionalDetails,
    };
    enqueue(videoScrollEvent);
    handleMarketingEvents(
      KissanEventType.REELS_SCROLL_VERTICAL_PAGE_END,
      { screen: 'feed page' },
      additionalDetails
    );
  };

  // Scroll handler for header animation

  const onViewableItemsChanged = useCallback(({ viewableItems }) => {
    // console.log('🚀 ~ viewableItems called:', viewableItems?.length);
    // FIRE IMPRESSION EVENTS FOR ALL TYPES
    viewableItems?.forEach((item) => {
      const itemValue = item?.item;
      const contentId = itemValue?.actionData?.content?.id ?? item.index;

      if (hasImpressionShowRef.current !== contentId) {
        hasImpressionShowRef.current = contentId;
        captureImpressionEvent(item.index, itemValue, itemValue?.type);
      }
    });

    // console.log('Is momentum scroll ended => ', isMomentumScrollEnded);

    // STREAM-SPECIFIC VIDEO PLAYBACK LOGIC (optional)
    const activeVideoIndex = getMinMaxIndexes(viewableItems, 'STREAM').activeVideoIndex;
    // const currentItem = viewableItems?.find((x) => x.type === 'STREAM');
    // // console.log('🚀 ~ activeVideoIndex:', viewableItems);

    // if (activeVideoIndex !== scrollToIndexRef.current) {
    //   console.log('No play video!!!');
    //   setVideoFeedIndex(-1);
    //   return;
    // }

    if (activeVideoIndex > -1 && activeVideoIndex !== Number.MAX_SAFE_INTEGER) {
      // console.log(
      //   '🚀 ~ onViewableItemsChanged ~ activeVideoIndex:',
      //   activeVideoIndex,
      //   isMomentumScrollEnded
      // );
      setVideoFeedIndex(activeVideoIndex);
    }

    if (viewableItems[0]?.index !== undefined) {
      const activeIndex = viewableItems[0].index as number;
      if (
        activeIndex % paginationCount.current.stream === 0 &&
        activeIndex >= paginationCount.current.stream
      ) {
        const itemValue = viewableItems?.[0]?.item;
        handleVideoScrollEvent(activeIndex, itemValue);
      }
    }
  }, []);

  const onFirstItemFullyVisible = useCallback(
    (visibleItem: { item: any; index: number | null; key: string }) => {
      // throw new Error('Function not implemented.');
      console.log('Activating video play at index => ', visibleItem);
      setVideoFeedIndex(visibleItem.index ?? -1);
    },
    [setVideoFeedIndex]
  );

  const captureImpressionEvent = (index: number, itemValue: any, type: string) => {
    const content = itemValue?.actionData?.content;
    const additionalDetails = [
      addEventParam('scrolledIndex', index),
      addEventParam('itemId', content?.id),
      addEventParam('videoId', content?.id),
      addEventParam('recommendationId', content?.recommendationId),
      addEventParam('videoSourceOrigin', content?.videoSourceOrigin),
      addEventParam('eventId', content?.eventId),
      addEventParam('type', type),
    ];

    if (content?.rankingId) {
      additionalDetails.push(
        addEventParam('referenceKey4', 'rankingId'),
        addEventParam('referenceValue4', content.rankingId)
      );
    }

    const event: INinjaEvent = {
      name: KissanEventType.DATA_VIEWED_MORETHAN_ONE_SECOND,
      screenDetails: { screen: 'feed page' },
      eventParams: additionalDetails,
    };
    enqueue(event);
  };

  const updateFollowState = useCallback(
    (newFollowState: boolean, postOwnerId: string) => {
      return data?.map((el) => {
        if (el?.type === 'STREAM' && el?.actionData?.content?.postOwnerId === postOwnerId) {
          return {
            ...el,
            actionData: {
              ...el.actionData,
              content: { ...el.actionData.content, following: newFollowState },
            },
          };
        }
        return el;
      });
    },
    [data]
  );

  const updateConnectionStatus = useCallback(
    async (isFollow: boolean, postOwnerId: string) => {
      const prevState = isFollow;
      const config = {
        type: isFollow ? 'unfollow' : 'follow',
        formData: {
          sourceObjectId: userProfile?.user?.id ?? '-1',
          sourceObjectType: 'USER',
          destinationObjectId: postOwnerId,
          destinationObjectType: 'USER',
          associationType: 'FOLLOWED',
        },
      };

      try {
        const response: boolean = await updateFollowStatusApi(config);
        // If API call succeeds, use the new state (opposite of current isFollow)
        if (response) {
          const newFollowState = response ? prevState : !prevState;
          const updatedHomeFeeds = updateFollowState(newFollowState, postOwnerId);
          setHomeFeedsCard(updatedHomeFeeds);
          console.log('Follow status updated successfully:', newFollowState);
        }
      } catch (error) {
        console.error('Error updating Connection Status', error);
        // On error, revert to original state
        const updatedHomeFeeds = updateFollowState(!isFollow, postOwnerId);
        setHomeFeedsCard(updatedHomeFeeds);
      } finally {
        setNeedRefresh(!needRefresh);
      }
    },
    [data, userProfile?.user?.id, setHomeFeedsCard, setNeedRefresh, needRefresh, updateFollowState]
  );

  // Render each feed item
  const renderItem = useCallback(
    ({ item, index }) => (
      <ListItemRenderer
        item={item}
        index={index}
        onCommentBottomSheetOpen={onCommentBottomSheetOpen}
        updateConnectionStatus={updateConnectionStatus}
        locationPermissionAsk={locationPermissionAsk}
        isWeatherWidgetAvailable={isWeatherWidgetAvailable}
        onReportBottomSheetOpen={onReportBottomSheetOpen}
        cropPreference={cropPreference}
        animalPreference={animalPreference}
        setVideoFeedIndex={setVideoFeedIndex}
      />
    ),
    [
      onCommentBottomSheetOpen,
      updateConnectionStatus,
      locationPermissionAsk,
      isWeatherWidgetAvailable,
      onReportBottomSheetOpen,
      cropPreference,
      animalPreference,
    ]
  );

  // const renderItem = useCallback(({ item, index }) => <></>, []);

  // Empty state component
  const ListEmptyComponent = useCallback(() => {
    if (refreshing) return null;

    return (
      <View
        flex={1}
        backgroundColor={'$light'}
        justifyContent="center"
        alignItems="center"
        padding={16}
        marginVertical={20}
        borderRadius={50}
        marginTop={150}
      >
        <NoSearchResult height={100} width={'100%'} />
        <Paragraph
          style={{
            fontSize: 22,
            fontWeight: '600',
            paddingTop: 10,
            color: '$color',
            marginTop: 20,
            marginBottom: 10,
          }}
        >
          {t('page.sorry')}
        </Paragraph>
        <Paragraph fontSize={16} fontWeight={'400'} color={'$color'} textAlign="center">
          {t('page.noDataFoundTryAfterSomeTime')}
        </Paragraph>
      </View>
    );
  }, [refreshing]);

  useEffect(() => {
    //Disable auto scroll for first item
    const scrollItemToIndex = () => {
      if (scrollToIndexRef.current !== scrollToIndex && disableScroll) {
        setTimeout(() => {
          scrollToIndexRef.current = scrollToIndex;
          disableScrollSet(false);
        }, 300);
        if (scrollOffsetRef.current > 200) {
          flashListRef.current?.scrollToIndex({
            index: scrollToIndex,
            animated: true,
            viewOffset: -60,
          });
        }
      }
    };
    const autoPlayfirstItem = data?.findIndex((x) => x.type === 'STREAM');
    if (scrollOffsetRef.current < 200) {
      setVideoFeedIndex(autoPlayfirstItem);
    } else {
      scrollItemToIndex();
    }
  }, [scrollToIndex, disableScroll]);

  // useEffect(() => {
  //   if (scrollToIndexRef.current === -1) {
  //     disableScrollSet(true);
  //     const autoPlayfirstItem = data?.findIndex((x) => x.type === 'STREAM');
  //     setVideoFeedIndex(autoPlayfirstItem ?? -1);
  //   }
  // }, [data]);

  const onListScroll = (offset: number) => {
    // console.log('On list scroll offset!!!', offset);
    scrollOffsetRef.current = offset;
  };

  return (
    <UniversalList
      ref={flashListRef}
      data={data}
      extraData={extraData}
      // contentContainerStyle={{ paddingTop: headerHeight }}
      renderItem={renderItem}
      getItemType={(item: IAdaptiveActionCard) => item?.type}
      keyExtractor={(item, index) => index.toString()}
      scrollEventThrottle={16}
      // onScroll={scrollHandler}
      viewabilityConfig={viewabilityConfig}
      onViewableItemsChanged={onViewableItemsChanged}
      estimatedItemSize={430}
      showsVerticalScrollIndicator={false}
      onEndReachedThreshold={1}
      onEndReached={onEndReached}
      ItemSeparatorComponent={Separator}
      refreshing={refreshing}
      onRefresh={onRefresh}
      ListFooterComponent={
        <YStack flex={1} padding="$3" alignItems="center">
          <Spinner size="large" color="$green10" />
        </YStack>
      }
      ListFooterComponentStyle={{ height: 144 }}
      ListEmptyComponent={ListEmptyComponent()}
      ListHeaderComponent={
        <HomeHeader
          isWeatherWidgetAvailable={isWeatherWidgetAvailable ?? false}
          globalSearchEnabled={globalSearchEnabled ?? false}
          headerHeight={headerHeight}
        />
      }
      onScroll={onListScroll}
      headerHeight={128}
      animatedHeader={true}
    />
  );
};
